import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { FC, useCallback, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { DatePickerEditCell } from 'src/presentations/components/organisms/contractManager/rateCardCharges/PickerEditCells';
import { ContractDto } from 'src/generated/services/TFinancialApi';
import { ContractFilters, useContractList, useContractsMetadata, useDeleteContract, useUpsertContract } from 'src/use-cases/settings/useContracts';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { useAllServiceProvidersDropdown } from 'src/use-cases/settings/useServiceProvider';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { useSettingsTable } from 'src/presentations/hooks/useSettingsTable';
import { FilterMapping, useTableFilters } from 'src/presentations/hooks/useTableFilters';
import { SettingsTableLayout } from 'src/presentations/components/templates/SettingsTableLayout';
import { ContractDetail } from './ContractDetail';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

// Filter mapping for contracts
const CONTRACT_FILTER_MAPPING: FilterMapping = {
  serviceProviderId: {
    filterKey: 'serviceProviderIds',
    transform: (values: string[]) => values.map(Number),
  },
  shipmentCount: {
    filterKey: 'shipmentCounts',
    transform: (values: string[]) => values.map(Number),
  },
  contractFlowCount: {
    filterKey: 'contractFlowCounts',
    transform: (values: string[]) => values.map(Number),
  },
  routeMappingCount: {
    filterKey: 'routeCounts',
    transform: (values: string[]) => values.map(Number),
  },
  zoneMappingCount: {
    filterKey: 'zoneCounts',
    transform: (values: string[]) => values.map(Number),
  },
  startDate: {
    filterKey: 'startDates',
  },
  endDate: {
    filterKey: 'endDates',
  },
};

interface ContractsTableProps {
  searchQuery?: string;
}

export const ContractsMain: FC<ContractsTableProps> = (props) => {
  const { searchQuery } = props;

  // Use the reusable settings table hook
  const {
    selectedEntityId,
    setSelectedEntityId,
    filters,
    entities,
    entitiesTotal,
    isLoading,
    isError,
    pagination,
    sorting,
    handleSortingChange,
    onChangePage,
    onChangePageSize,
    getTextFieldProps,
    clearValidationErrors,
    updateFilter,
    handleTableSort,
    metadataData,
    refetch,
    refetchMetadata,
    handleEntitySave,
  } = useSettingsTable<ContractDto, ContractFilters>({
    entityName: 'Contract',
    requiredFields: ['serviceProviderId', 'startDate', 'endDate'],
    useEntityList: useContractList,
    useEntityMetadata: useContractsMetadata,
    useUpsertEntity: useUpsertContract as any, // Type assertion needed due to DTO transformation
    searchQuery,
    initialSorting: [{ id: 'startDate', desc: true }],
    // Transform ContractDto to ContractUpsertRequestDto
    prepareEntityBody: (values, originalRow) => {
      // Use original row data as fallback for missing values
      const serviceProviderId = values.serviceProviderId ?? originalRow?.serviceProviderId;
      const startDate = values.startDate ?? originalRow?.startDate;
      const endDate = values.endDate ?? originalRow?.endDate;

      // DatePicker stores Date objects in _valuesCache; format to yyyy-MM-dd for API
      const formatDate = (val: unknown): string | undefined => {
        if (!val) return undefined;
        if (val instanceof Date) return format(val, 'yyyy-MM-dd');
        if (typeof val === 'string') {
          // Already ISO format - return as-is
          const d = new Date(val);
          return Number.isNaN(d.getTime()) ? undefined : format(d, 'yyyy-MM-dd');
        }
        return undefined;
      };

      return {
        id: values.id ?? originalRow?.id,
        serviceProviderId: serviceProviderId ? Number(serviceProviderId) : undefined,
        active: values.active ?? originalRow?.active ?? true,
        filePath: values.filePath ?? originalRow?.filePath,
        startDate: formatDate(startDate) as string,
        endDate: formatDate(endDate) as string,
        code: values.code ?? originalRow?.code,
        contractFlowId: values.contractFlowId ?? originalRow?.contractFlowId,
      } as any;
    },
    // Custom request body preparation for contracts
    prepareRequestBody: (filters, pagination, sorting) => {
      let sortingDirection: string | undefined;
      if (sorting.length > 0) {
        sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';
      }

      return {
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortBy: sorting.length > 0 ? sorting[0].id : undefined,
        sortDirection: sortingDirection,
        ...filters,
      };
    },
  });

  const { mutateAsync: deleteContract } = useDeleteContract();

  const handleDelete = useCallback(
    async (row: MRT_Row<ContractDto>) => {
      // eslint-disable-next-line no-alert
      if (!globalThis.confirm('Are you sure you want to delete this contract?')) return;
      try {
        await deleteContract({ contractId: String(row.original.id) });
        openToast({ toastType: 'success', title: 'Success', description: 'Contract deleted!' });
      } catch (error) {
        openToastError(error instanceof Error ? error : new Error('Contract delete failed'));
      }
    },
    [deleteContract]
  );

  // Use the reusable table filters hook
  const { handleTableFilter } = useTableFilters<ContractFilters>({
    updateFilter,
    refetch,
    refetchMetadata,
  });

  // Service providers for Cell display and Autocomplete dropdown
  const { data: spData } = useAllServiceProvidersDropdown();
  const spOptions = useMemo(
    () =>
      spData?.data?.content?.map((sp) => ({
        label: sp.code ?? sp.name,
        value: sp.id as number,
        name: sp.name,
        code: sp.code,
      })) ?? [],
    [spData]
  );

  const columns = useMemo(
    (): MRT_ColumnDef<ContractDto>[] => [
      {
        accessorKey: 'serviceProviderId',
        header: 'Service Provider',
        enableSorting: false,
        Header: ({ column }) => {
          const spMetadata = metadataData?.data?.serviceProviderIds ?? {};
          const filterItems = Object.entries(spMetadata)
            .map(([spId, count]) => {
              const sp = spOptions.find((o) => o.value === Number(spId));
              return {
                id: spId,
                label: sp ? sp.code ?? sp.name : spId,
                count: Number(count),
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.serviceProviderIds?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => {
          const id = cell.getValue() as number;
          const sp = spOptions.find((o) => o.value === id);
          return sp ? sp.code ?? sp.name : id?.toString() ?? '-';
        },
        Edit: ({ column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;
          const currentId = (row._valuesCache[column.id] ?? row.original.serviceProviderId) as number | undefined;
          const selectedOption = spOptions.find((o) => o.value === currentId) ?? null;
          return (
            <Autocomplete
              options={spOptions}
              getOptionLabel={(option) => option.label}
              value={selectedOption}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              onChange={(_, newValue) => {
                row._valuesCache[column.id] = newValue?.value ?? null;
                if (isCreating) table.setCreatingRow(row);
                else if (isEditing) table.setEditingRow(row);
              }}
              renderInput={(params) => <TextField {...params} variant='standard' placeholder='Search...' size='small' required />}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': { fontSize: '12px' },
                },
              }}
              sx={{ minWidth: 220 }}
            />
          );
        },
      },
      {
        accessorKey: 'shipmentCount',
        header: 'Shipment Count',
        enableEditing: false,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.shipmentCounts);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.shipmentCounts?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => ((cell.getValue() as number) || 0).toLocaleString(),
      },
      {
        accessorKey: 'contractFlowCount',
        header: 'Shipping Service Count',
        enableEditing: false,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.contractFlowCounts);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.contractFlowCounts?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => ((cell.getValue() as number) || 0).toString(),
      },
      {
        accessorKey: 'routeMappingCount',
        header: 'Route Count',
        enableEditing: false,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.routeMappingCounts);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.routeCounts?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => ((cell.getValue() as number) || 0).toString(),
      },
      {
        accessorKey: 'zoneMappingCount',
        header: 'Zone Count',
        enableEditing: false,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.zoneMappingCounts);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.zoneCounts?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => ((cell.getValue() as number) || 0).toString(),
      },
      {
        accessorKey: 'startDate',
        header: 'Start Date',
        enableSorting: true,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.startDates);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.startDates}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => {
          const val = cell.getValue() as string;
          return val ? format(parseISO(val), 'd-MMM-yyyy') : '-';
        },
        Edit: ({ column, row, table }) => <DatePickerEditCell column={column} row={row} table={table} fieldKey='startDate' />,
      },
      {
        accessorKey: 'endDate',
        header: 'End Date',
        enableSorting: true,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.endDates);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.endDates}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => {
                handleTableFilter(columnId, selectedValues, filterItems, CONTRACT_FILTER_MAPPING);
              }}
            />
          );
        },
        Cell: ({ cell }) => {
          const val = cell.getValue() as string;
          return val ? format(parseISO(val), 'd-MMM-yyyy') : '-';
        },
        Edit: ({ column, row, table }) => <DatePickerEditCell column={column} row={row} table={table} fieldKey='endDate' />,
      },
    ],
    [
      spOptions,
      handleTableSort,
      metadataData?.data,
      handleTableFilter,
      filters.serviceProviderIds,
      filters.shipmentCounts,
      filters.contractFlowCounts,
      filters.routeCounts,
      filters.zoneCounts,
      filters.startDates,
      filters.endDates,
    ]
  );

  // Custom cell props to handle row clicks for detail panel
  const customCellProps = ({ row, column, table }) => ({
    onClick: () => {
      // Do not open detail panel if row is in editing/creating mode
      const { editingRow, creatingRow } = table.getState();
      const isEditing = editingRow?.id === row.id || !!creatingRow;
      if (column?.id !== 'mrt-row-actions' && !isEditing) {
        setSelectedEntityId(row.original.id);
      }
    },
    sx: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        textDecoration: 'underline',
      },
    },
  });

  return (
    <SettingsTableLayout
      title='Contracts Settings'
      subtitle='Configure contract settings'
      backPath='/settings'
      columns={columns}
      data={entities}
      totalItems={entitiesTotal}
      isLoading={isLoading}
      isError={isError}
      pagination={pagination}
      sorting={sorting}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      onSortingChange={handleSortingChange}
      onSaveEntity={handleEntitySave}
      onCancelEdit={clearValidationErrors}
      onDeleteRow={handleDelete}
      selectedEntityId={selectedEntityId}
      onCloseDetail={() => setSelectedEntityId(null)}
      detailPanelTop={13}
      renderDetail={(entityId) => <ContractDetail contractId={entityId} onClose={() => setSelectedEntityId(null)} />}
      customCellProps={customCellProps}
      enableRowSelection={false}
    />
  );
};
